import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ChevronDown,
  Droplet,
  Wrench,
  Search,
  
  Home,
  Paintbrush,
  Hammer,
  Layers,
  Square,
  Building2,
  Grid3x3,
  Sun,
  ShieldCheck,
  Users,
  Eye,
  Clock,
  Handshake,
  BadgeCheck,
  Star,
  ArrowRight,
} from "lucide-react";
import { Container } from "@/components/site/Container";
import { ButtonLink } from "@/components/site/Button";
import { useInspectionDialog } from "@/components/site/InspectionDialog";
import { PageHero } from "@/components/site/PageHero";
import { InstantEstimateStrip } from "@/components/site/InstantEstimateStrip";
import { businessNode, canonical, webSiteNode } from "@/lib/seo";
import { ogImageMeta } from "@/lib/og-image";
import { type SiteSettings } from "@/lib/site-settings.functions";
import { loadPageSettings } from "@/lib/page-seo-loader";
import { applySeoOverrides } from "@/lib/seo-overrides";



const faqs = [
  {
    q: "Do you offer free roof inspections in Phoenix?",
    a: "Yes. Durafoam Roofing offers free roof inspections for homeowners, buyers, sellers, and monsoon prep across Phoenix and the surrounding Valley.",
  },
  {
    q: "How do I know if my foam roof needs recoating?",
    a: "Common signs include a chalky or cracking coating surface, thin spots where foam is starting to show through, or a roof that's more than 5 years old without recoating. We inspect for free and give you an honest assessment.",
  },
  {
    q: "How much does foam roof repair cost in Phoenix?",
    a: "Most foam roof repairs range from $300–$1,500 depending on the size and severity. We assess the roof first and give you a clear estimate before any work begins.",
  },
  {
    q: "Do you work on tile roofs as well as foam roofs?",
    a: "Yes. We repair, replace, and maintain foam roofs, tile roofs, shingle roofs, flat roofs, and skylights throughout the Phoenix Valley.",
  },
  {
    q: "Are you licensed and insured?",
    a: "Yes. Durafoam Roofing is fully licensed (ROC223900 L-42), bonded, and insured in Arizona. We have been BBB accredited since 2008.",
  },
  {
    q: "How quickly can you respond to a roof leak?",
    a: "We respond to emergency roof leaks quickly, especially during monsoon season. Call us and we will get back to you the same day.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

function ratingSchema(rating: number, reviewCount: number) {
  return businessNode({
    aggregateRating: {
      ratingValue: rating.toFixed(1),
      reviewCount: String(reviewCount),
    },
  });
}


export const Route = createFileRoute("/")({
  loader: () => loadPageSettings("home"),
  head: ({ loaderData }) => {
    const rating = loaderData?.rating ?? 4.8;
    const reviewCount = loaderData?.reviewCount ?? 84;
    return applySeoOverrides({
      meta: [
        { title: "Durafoam Roofing | Phoenix Roofing Contractor Since 1989" },
        {
          name: "description",
          content:
            "Phoenix's spray foam roofing specialists since 1989. 4.8★, 84 Google reviews, free roof inspections. Know what's going on up there. ROC223900.",
        },
        { property: "og:title", content: "Durafoam Roofing | Phoenix Roofing Contractor Since 1989" },
        {
          property: "og:description",
          content:
            "Phoenix's spray foam roofing specialists since 1989. 4.8★, 84 Google reviews, free roof inspections. Know what's going on up there. ROC223900.",
        },
        { property: "og:url", content: canonical("/") },
        { property: "og:type", content: "website" },
        ...ogImageMeta("home"),
        { name: "twitter:title", content: "Durafoam Roofing | Phoenix Roofing Contractor Since 1989" },
        {
          name: "twitter:description",
          content:
            "Phoenix's spray foam roofing specialists since 1989. 4.8★, 84 Google reviews, free roof inspections. Know what's going on up there. ROC223900.",
        },

      ],

      links: [{ rel: "canonical", href: canonical("/") }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(webSiteNode()) },
        { type: "application/ld+json", children: JSON.stringify(faqSchema) },
        { type: "application/ld+json", children: JSON.stringify(ratingSchema(rating, reviewCount)) },
      ],
    }, loaderData?.seo);
  },
  component: HomePage,
});

function HomePage() {
  const settings = Route.useLoaderData();
  return (
    <>
      <InstantEstimateStrip />
      <Hero />
      <ProblemSelector />
      <Services />
      <WhyUs />
      <Reviews settings={settings} />
      <CostPreview />
      <FAQ />
      <FinalCTA />
    </>
  );
}


/* ---------- Hero ---------- */
/** Mascot accent in the hero: "subtle" | "bold" | undefined (off). */
const HERO_MASCOT: "subtle" | "bold" | undefined = "bold";

function Hero() {
  const { openInspection } = useInspectionDialog();
  return (
    <PageHero
      pageSlug="home"
      mascot={HERO_MASCOT}
      eyebrow="Phoenix Roofing Since 1989"
      h1={
        <>
          Foam, Tile &amp; Shingle
          <br />
          <span className="text-accent">Roofing Done Right.</span>
        </>
      }
      subhead={
        <>
          Leaks, storm damage, or an aging roof? We&rsquo;ll inspect it, document what we find, and give you a
          straight answer.
        </>
      }
      actions={
        <>
          <ButtonLink
            href="/free-roof-inspection-phoenix"
            variant="primary"
            size="lg"
            className="px-8 py-3.5 text-[16px] min-h-[52px] shadow-[0_10px_24px_-8px_rgba(244,121,32,0.7)] sm:py-4 sm:text-[17px] sm:min-h-[58px]"
            onClick={(e) => { e.preventDefault(); openInspection(); }}
          >
            Schedule a Free Roof Check
          </ButtonLink>
          <ButtonLink
            href="tel:+14809415373"
            variant="ghost-light"
            size="lg"
            className="min-h-[52px] px-7 text-[16px] sm:min-h-[58px] sm:text-[17px]"
          >
            <span className="sm:hidden">Call Us</span>
            <span className="hidden sm:inline">Call 480-941-5373</span>
          </ButtonLink>
        </>
      }
    >
      <p className="mt-4 text-[12px] font-semibold uppercase tracking-[0.14em] text-white/70 sm:mt-7 sm:text-[13px]">
        Don&rsquo;t just foam your roof. <span className="text-white">Durafoam it.</span>
      </p>
    </PageHero>
  );
}




/* ---------- Problem selector ---------- */
function ProblemSelector() {
  const { openInspection } = useInspectionDialog();
  const cards: Array<{
    icon: typeof Droplet;
    title: string;
    sub: string;
    cta: string;
    href: string;
    button: boolean;
    popup?: boolean;
  }> = [
    {
      icon: Droplet,
      title: "I have a roof leak",
      sub: "Water stains, drips, or moisture after rain",
      cta: "Learn about leak repair",
      href: "/roof-leak-repair",
      button: false,
    },
    {
      icon: Wrench,
      title: "My roof looks worn out",
      sub: "Cracks, bubbles, chalky coating, or exposed foam",
      cta: "Learn about foam recoating",
      href: "/foam-roof-coating-recoating",
      button: false,
    },
    {
      icon: Home,
      title: "I need a roof replacement",
      sub: "Foam, tile, shingle, or foaming over an existing flat roof",
      cta: "Explore replacement options",
      href: "/roof-replacement-phoenix",
      button: false,
    },
    {
      icon: Search,
      title: "I need a roof inspection",
      sub: "Buying, selling, refinancing, or monsoon prep",
      cta: "Schedule free inspection",
      href: "/free-roof-inspection-phoenix",
      button: true,
      popup: true,
    },
  ];

  return (
    <section className="bg-background py-16 lg:py-24">
      <Container>
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-[26px] lg:text-4xl font-semibold">What's going on with your roof?</h2>
          <p className="mt-4 text-[var(--muted-foreground)]">
            Tell us what you're dealing with — we'll point you in the right direction.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2">
          {cards.map(({ icon: Icon, title, sub, cta, href, button, popup }) => (
            <Link
              key={title}
              to={href}
              onClick={popup ? (e) => { e.preventDefault(); openInspection(); } : undefined}
              className={`group block rounded-xl border-[1.5px] bg-white p-6 transition-colors duration-150 hover:border-accent ${button ? "border-accent" : "border-border"}`}
            >
              <div className="flex items-start gap-4">
                <span
                  aria-hidden="true"
                  className="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-[var(--surface)] text-accent group-hover:bg-accent group-hover:text-white transition-colors"
                >
                  <Icon className="h-6 w-6" />
                </span>
                <div className="min-w-0">
                  <h3 className="text-[18px] lg:text-[22px] font-semibold">{title}</h3>
                  <p className="mt-1.5 text-[15px] text-[var(--muted-foreground)] leading-snug">
                    {sub}
                  </p>
                  {button ? (
                    <span className="mt-4 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-accent px-6 py-3 text-[15px] font-semibold text-primary transition-colors duration-150 group-hover:bg-[var(--accent-hover)]">
                      {cta} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </span>
                  ) : (
                    <span className="mt-4 inline-flex items-center gap-1.5 text-[14px] font-semibold text-primary group-hover:text-accent">
                      {cta} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ---------- Services ---------- */
function Services() {
  const services = [
    { icon: Home, title: "Spray Foam Roofing", desc: "New SPF foam roofing systems built to handle Phoenix sun. Seamless, lightweight, and energy efficient.", href: "/foam-roofing" },
    { icon: Paintbrush, title: "Foam Roof Recoating", desc: "Restore and extend the life of your foam roof with a fresh elastomeric coating. Every 5 years is standard in Phoenix.", href: "/foam-roof-coating-recoating" },
    { icon: Wrench, title: "Foam Roof Repair", desc: "Foam roof repair for leaks, blisters, and bird damage — on any SPF roof, no matter who installed it.", href: "/foam-roof-repair-phoenix" },

    { icon: Droplet, title: "Roof Leak Repair", desc: "Same-day response for active leaks. We find the source, stop the water, and document the repair.", href: "/roof-leak-repair" },
    { icon: Layers, title: "Tile Roofing", desc: "Tile repair, replacement, and underlayment work for Phoenix homes. Match your existing tile or upgrade completely.", href: "/tile-roofing" },
    { icon: Square, title: "Flat Roof Repair", desc: "Repairs and recoats for flat residential and commercial roofs. Pinpoint leak fixes and full restorations.", href: "/flat-roof-repair" },
    { icon: Building2, title: "Commercial Roofing", desc: "Foam, single-ply, and built-up systems for Phoenix commercial buildings, HOAs, and property managers.", href: "/commercial-roofing" },
    { icon: Grid3x3, title: "Shingle Roofing", desc: "Asphalt shingle repair and replacement built for Arizona heat and high-UV conditions.", href: "/shingle-roofing" },
    { icon: Sun, title: "Skylight Repair", desc: "Skylight leak sealing, flashing repair, and replacement for residential and commercial roofs.", href: "/skylight-repair" },
    { icon: Hammer, title: "Roof Maintenance", desc: "Scheduled inspections and tune-ups that catch small problems before monsoon season turns them into big ones.", href: "/roof-maintenance" },
  ];

  return (
    <section className="bg-[var(--surface)] py-16 lg:py-24">
      <Container>
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-[26px] lg:text-4xl font-semibold">Roofing services built for Phoenix homes</h2>
          <p className="mt-4 text-[var(--muted-foreground)]">
            Residential and commercial roofing across the Phoenix Valley.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.map(({ icon: Icon, title, desc, href }) => (
            <article
              key={title}
              className="relative rounded-xl border border-border bg-white p-6 pt-7 transition-colors duration-150 hover:border-primary"
            >
              <span aria-hidden="true" className="absolute top-0 left-6 h-1.5 w-10 -translate-y-1/2 rounded-full bg-accent" />
              <Icon className="h-7 w-7 text-primary" aria-hidden="true" />
              <h3 className="mt-4 text-[20px] font-semibold">{title}</h3>
              <p className="mt-2 text-[15px] text-[var(--muted-foreground)] leading-relaxed">{desc}</p>
              <Link
                to={href}
                aria-label={`Learn more about ${title.toLowerCase()}`}
                className="mt-4 inline-flex items-center gap-1.5 text-[14px] font-semibold text-primary hover:text-accent"
              >
                Learn more <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </article>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ---------- Why us ---------- */
function WhyUs() {
  const { openInspection } = useInspectionDialog();
  const items = [
    { icon: Users, title: "Family-Owned Since 1989", desc: "Family-owned since 1989 — the same family, same name, same standards." },
    { icon: Eye, title: "Honest Roof Assessments", desc: "We tell you what your roof actually needs — not what's easiest to sell you." },
    { icon: ShieldCheck, title: "Foam Roof Specialists", desc: "SPF foam isn't a side service for us. It's what we've built our reputation on." },
    { icon: Clock, title: "Fast Leak Response", desc: "Active leak? Call and we'll respond the same day, especially in monsoon season." },
    { icon: Handshake, title: "No High-Pressure Sales", desc: "Free inspection, clear options, and a quote you can take time to think about." },
    { icon: BadgeCheck, title: "Licensed, Bonded & Insured", desc: "Arizona ROC223900 L-42. BBB accredited since 2008. Rosie on the House certified." },
  ];

  return (
    <section className="bg-primary py-16 lg:py-24 text-white">
      <Container>
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-[26px] lg:text-4xl font-semibold text-white">
            Why Phoenix homeowners call Durafoam first
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
          {items.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="flex gap-4">
              <span
                aria-hidden="true"
                className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-white/10 text-accent"
              >
                <Icon className="h-5.5 w-5.5" />
              </span>
              <div>
                <h3 className="text-[18px] font-semibold text-white">{title}</h3>
                <p className="mt-1.5 text-[15px] text-white/75 leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-14 text-center">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-center sm:items-center">
            <ButtonLink
              href="/free-roof-inspection-phoenix"
              variant="primary"
              size="lg"
              className="px-9 py-4 text-lg min-h-[56px] shadow-lg"
              onClick={(e) => { e.preventDefault(); openInspection(); }}
            >
              Book Your Free Inspection
            </ButtonLink>
            <ButtonLink href="tel:+14809415373" variant="ghost-light" size="md">
              <span className="sm:hidden">Call Us</span>
              <span className="hidden sm:inline">Call 480-941-5373</span>
            </ButtonLink>
          </div>
          <p className="mt-5 text-[13px] text-white/60">
            Free inspections · No pressure · Family-owned since 1989
          </p>
        </div>
      </Container>
    </section>
  );
}

/* ---------- Reviews ---------- */
function StarRow({ rating }: { rating: number }) {
  // Render 5 stars with fractional fill for the partial star.
  return (
    <div className="inline-flex items-center">
      <span className="sr-only">{rating.toFixed(1)} out of 5 stars</span>
      <div className="flex gap-0.5" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => {
          const fill = Math.max(0, Math.min(1, rating - i));
          return (
            <span key={i} className="relative inline-block h-5 w-5">
              <Star className="absolute inset-0 h-5 w-5 text-accent/30" />
              <span
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${fill * 100}%` }}
              >
                <Star className="h-5 w-5 fill-accent text-accent" />
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

function Reviews({ settings }: { settings: SiteSettings }) {
  const reviews = [
    {
      quote:
        "Every company should be run like DuraFoam Roofing. This is the second roof they did for me and they are fast, clean and always courteous. On one of my homes they even replaced skylights because of a terrible hail storm. Also a big shout out to Dave my rep who's always available. No need to go anywhere else!",
      name: "Phoenix homeowner",
      area: "Phoenix, AZ",
    },
    {
      quote:
        "Living in a flat roofed home community for over 43 years, we know how important roof maintenance is! We started using Durafoam many, many years ago for their elastomeric roof coating and have continued using them for roof maintenance and their warranty program. They are very professional, personable, and do a fantastic job!",
      name: "Sun City homeowner",
      area: "Sun City, AZ",
    },
    {
      quote:
        "I've worked with Durafoam for my roofing needs for many years at my home, office, and rental properties. Always high quality work, competitive pricing, and they do what they say they will and when.",
      name: "Phoenix homeowner",
      area: "Phoenix, AZ",
    },
  ];

  return (
    <section className="bg-background py-16 lg:py-24">
      <Container>
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-[26px] lg:text-4xl font-semibold">
            Trusted by Phoenix homeowners since 1989
          </h2>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <StarRow rating={settings.rating} />
            <p className="text-[15px] font-semibold text-primary">
              {settings.rating.toFixed(1)} out of 5
            </p>
            <span aria-hidden="true" className="text-[var(--muted-foreground)]">·</span>
            <p className="text-[15px] text-[var(--muted-foreground)]">
              {settings.reviewCount} Google reviews
            </p>
          </div>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-5 lg:grid-cols-3">
          {reviews.map((r, i) => (
            <figure
              key={i}
              className="flex flex-col rounded-xl border border-border bg-white p-6"
            >
              <div>
                <span className="sr-only">5 out of 5 stars</span>
                <div className="flex gap-0.5" aria-hidden="true">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-4.5 w-4.5 fill-accent text-accent" />
                  ))}
                </div>
              </div>
              <blockquote className="mt-4 flex-1 text-[15px] italic leading-relaxed text-foreground">
                "{r.quote}"
              </blockquote>
              <figcaption className="mt-5 border-t border-border pt-4">
                <p className="font-semibold text-foreground">{r.name}</p>
                <p className="text-[13px] text-[var(--muted-foreground)]">
                  {r.area} · Verified Google review
                </p>
              </figcaption>
            </figure>
          ))}
        </div>
        <div className="mt-10 text-center">
          <ButtonLink
            href="https://share.google/BLkRilLBeImSPBC7R"
            target="_blank"
            rel="noopener noreferrer"
            variant="ghost"
          >
            See all reviews on Google
          </ButtonLink>
        </div>
      </Container>
    </section>
  );
}

/* ---------- Cost preview ---------- */
function CostPreview() {
  return (
    <section className="bg-[var(--surface)] py-16 lg:py-24">
      <Container>
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-[26px] lg:text-4xl font-semibold">
            Curious what your project might cost?
          </h2>
          <p className="mt-4 text-[var(--muted-foreground)]">
            Roofing costs in Phoenix vary by roof size, condition, and material — recoats typically
            run a fraction of full replacement cost. We'll give you exact numbers after a free
            inspection, no guesswork. Cost the only thing holding you back?{" "}
            <Link to="/financing" className="underline">
              Ask us about payment options
            </Link>
            . Hail or monsoon damage?{" "}
            <Link to="/storm-damage-repair" className="underline">
              We work directly with your insurance
            </Link>
            .
          </p>
          <div className="mt-8">
            <ButtonLink href="/roofing-costs-phoenix" variant="primary" size="lg">
              See Phoenix roofing cost ranges <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </ButtonLink>
          </div>
        </div>
      </Container>
    </section>
  );
}


/* ---------- FAQ ---------- */

function FAQ() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="bg-background py-16 lg:py-24">
      <Container className="max-w-3xl">
        <div className="text-center">
          <h2 className="text-[26px] lg:text-4xl font-semibold">
            Common questions about Phoenix roofing
          </h2>
        </div>
        <div className="mt-10 divide-y divide-border border-y border-border">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={f.q}>
                <h3>
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between gap-4 py-5 text-left text-[17px] font-semibold text-primary"
                  >
                    <span>{f.q}</span>
                    <ChevronDown
                      className={`h-5 w-5 shrink-0 text-primary transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                      aria-hidden="true"
                    />
                  </button>
                </h3>
                {isOpen && (
                  <div className="pb-5 -mt-1 pr-9 text-[15px] text-foreground leading-relaxed">
                    {f.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

/* ---------- Final CTA ---------- */
function FinalCTA() {
  const { openInspection } = useInspectionDialog();
  return (
    <section className="bg-primary py-16 lg:py-24 text-white">
      <Container className="max-w-3xl text-center">
        <h2 className="text-[26px] lg:text-4xl font-semibold text-white">Ready to protect your roof?</h2>
        <p className="mt-4 text-lg text-white/75">
          Get a free, no-pressure roof inspection from a family-owned Phoenix team that has been
          doing this right since 1989.
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-center">
          <ButtonLink
            href="/free-roof-inspection-phoenix"
            variant="primary"
            size="lg"
            onClick={(e) => { e.preventDefault(); openInspection(); }}
          >
            Schedule free inspection
          </ButtonLink>
          <ButtonLink href="tel:+14809415373" variant="ghost-light" size="lg">
            <span className="sm:hidden">Call Us</span>
            <span className="hidden sm:inline">Call 480-941-5373</span>
          </ButtonLink>
          <ButtonLink href="sms:+14809415373" variant="ghost-light" size="lg" className="sm:hidden">
            Text Us
          </ButtonLink>
        </div>
        <p className="mt-6 text-[13px] text-white/60">
          Free inspections · No pressure · Honest assessments · Family-owned since 1989
        </p>
      </Container>
    </section>
  );
}
